import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Radiator add-ons configurator (blocks/cr-addons.liquid).
 *
 * Choices live in a side <dialog>. Each chosen add-on is written as hidden
 * `items[n][...]` inputs joined to Horizon's product form through the `form`
 * attribute, so the radiator and its add-ons go to /cart/add.js in ONE request.
 * Shopify accepts `id` and `items[]` together and adds all of them or none of
 * them, so a failed add-on can never leave a radiator in the cart on its own.
 *
 * @extends {Component<{ dialog: HTMLDialogElement, chosen: HTMLElement, total: HTMLElement, editLabel: HTMLElement, formFields: HTMLElement, emptyText?: HTMLElement }>}
 */
class CrAddons extends Component {
  requiredRefs = ['dialog', 'chosen', 'total', 'editLabel', 'formFields'];

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('change', this.#update);
    this.refs.dialog.addEventListener('click', this.#onBackdropClick);
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#section?.addEventListener('cr:product-synced', this.#update);
    this.#section?.addEventListener('input', this.#onQuantityInput);
    this.#section?.addEventListener('change', this.#onQuantityInput);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('change', this.#update);
    this.refs.dialog.removeEventListener('click', this.#onBackdropClick);
    this.#section?.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#section?.removeEventListener('cr:product-synced', this.#update);
    this.#section?.removeEventListener('input', this.#onQuantityInput);
    this.#section?.removeEventListener('change', this.#onQuantityInput);
  }

  open() {
    this.refs.dialog.showModal();
  }

  close() {
    this.refs.dialog.close();
  }

  /** @returns {HTMLInputElement[]} */
  get #selected() {
    return Array.from(this.refs.dialog.querySelectorAll('input[type="radio"]:checked')).filter(
      (input) => input instanceof HTMLInputElement && input.value !== ''
    );
  }

  get #radiatorQuantity() {
    const input = this.#section?.querySelector(`form#${CSS.escape(this.dataset.formId ?? '')} input[name="quantity"]`);
    const value = input instanceof HTMLInputElement ? Number(input.value) : 1;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  get #radiatorPrice() {
    const el = this.#section?.querySelector('[data-cr-radiator-price]');
    return el instanceof HTMLElement ? Number(el.dataset.crRadiatorPrice) || 0 : 0;
  }

  get #radiatorLabel() {
    const el = this.#section?.querySelector('[data-cr-radiator-price]');
    return (el instanceof HTMLElement && el.dataset.crRadiatorLabel) || this.dataset.radiatorTitle || '';
  }

  /**
   * Formats minor units exactly like Liquid's `money` filter in the visitor's
   * currency, using a sample the block renders server-side (`123400 | money`),
   * e.g. "£1,234.00", "€1.234,00" or "£1,234".
   * @param {number} cents
   */
  #money(cents) {
    const sample = this.dataset.moneySample ?? '';
    const match = sample.match(/1(\D?)234(?:(\D)00)?/);
    if (!match || match.index === undefined) return (cents / 100).toFixed(2);

    const [token, thousands = '', decimal] = match;
    const prefix = sample.slice(0, match.index);
    const suffix = sample.slice(match.index + token.length);
    const value = decimal ? (cents / 100).toFixed(2) : String(Math.round(cents / 100));
    const [whole = '0', fraction] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return `${prefix}${grouped}${decimal && fraction ? decimal + fraction : ''}${suffix}`;
  }

  #onQuantityInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') this.#update();
  };

  #update = () => {
    const quantity = this.#radiatorQuantity;
    const selected = this.#selected;
    let total = this.#radiatorPrice * quantity;

    this.refs.chosen.replaceChildren(
      ...selected.map((input) => {
        const perRadiator = Number(input.dataset.quantity) || 1;
        const lineQuantity = perRadiator * quantity;
        const linePrice = (Number(input.dataset.price) || 0) * lineQuantity;
        total += linePrice;
        const li = document.createElement('li');
        const name = document.createElement('span');
        name.textContent = `${lineQuantity > 1 ? `${lineQuantity} × ` : ''}${input.dataset.title ?? ''}`;
        const price = document.createElement('span');
        price.textContent = this.#money(linePrice);
        li.append(name, price);
        return li;
      })
    );

    this.refs.dialog.querySelectorAll('[data-group-choice]').forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      const group = label.dataset.groupChoice;
      const choice = selected.find((input) => input.dataset.group === group);
      label.textContent = choice?.dataset.title ?? label.dataset.noneLabel ?? '';
    });

    this.refs.total.textContent = this.#money(total);
    if (this.refs.emptyText) this.refs.emptyText.hidden = selected.length > 0;

    this.#writeFormFields(selected, quantity);
  };

  /**
   * Mirrors the chosen add-ons into the product form as `items[n]` inputs.
   * @param {HTMLInputElement[]} selected
   * @param {number} quantity
   */
  #writeFormFields(selected, quantity) {
    const formId = this.dataset.formId ?? '';
    const propertyName = this.dataset.forLabel ?? '';
    const label = this.#radiatorLabel;

    /** @param {string} name @param {string} value */
    const field = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.setAttribute('form', formId);
      return input;
    };

    this.refs.formFields.replaceChildren(
      ...selected.flatMap((input, index) => {
        const fields = [
          field(`items[${index}][id]`, input.value),
          field(`items[${index}][quantity]`, String((Number(input.dataset.quantity) || 1) * quantity)),
        ];
        if (propertyName && label) fields.push(field(`items[${index}][properties][${propertyName}]`, label));
        return fields;
      })
    );
  }

  /** @param {MouseEvent} event */
  #onBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    if (event.action !== 'add' || !(event.target instanceof Element)) return;
    if (!event.target.closest('product-form-component')) return;
    const count = this.#selected.length;
    if (count === 0) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        document.dispatchEvent(new CustomEvent('cr:track', { detail: { event: 'addons_added', count } }));
      })
      .catch(() => {});
  };
}

if (!customElements.get('cr-addons')) customElements.define('cr-addons', CrAddons);
